import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["custom"],
  _excluded2 = ["crmObject"],
  _excluded3 = ["custom"],
  _excluded4 = ["crmObject"],
  _excluded5 = ["actionId", "data"],
  _excluded6 = ["children"],
  _excluded7 = ["children"];
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import I18n from 'I18n';
import { useAuthInfoProvider } from 'content-management-data-lib';
import { ContentActions } from '../actions/actionConfigs';
import BulkUpdateErrorsListModal from '../modals/BulkUpdateErrorsListModal';
import withConvertingCrmObjectPropToArray from '../modals/withConvertingCrmObjectPropToArray';
import { mergeActionConfig } from '../utils/actionConfigUtils';
import { PermissionsExtraDataProvider, usePermissionsExtraData } from './PermissionsExtraDataProvider';
import LockedActionButton from '../components/LockedActionButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useContentActionsValue = ({
  actionsConfigOverrides = [],
  onActionError,
  onActionSuccess,
  actionSuccessHandlerOverrides = {},
  primaryActionId,
  secondaryActionId,
  useActionSuccessUpdateHandlers
}) => {
  const [isBulkUpdateErrorsModalOpen, setIsBulkUpdateErrorsModalOpen] = useState(false);
  const [bulkUpdateErrorsData, setBulkUpdateErrorsData] = useState({
    results: [],
    actionId: ''
  });
  const {
    userInfo: {
      gates,
      user: {
        scopes
      }
    }
  } = useAuthInfoProvider();
  const permissionsExtraDataForPlatform = usePermissionsExtraData();
  const allActions = useMemo(() => {
    return actionsConfigOverrides.reduce((acc, actionOverride) => {
      const {
        id
      } = actionOverride;
      const defaultActionConfig = acc[id];
      if (defaultActionConfig) {
        acc[id] = mergeActionConfig(defaultActionConfig, actionOverride);
      } else {
        acc[id] = Object.assign({}, actionOverride, {
          name: actionOverride.name || I18n.text('content-management-actions-lib.actions.unnamedAction')
        });
      }
      return acc;
    }, Object.assign({}, ContentActions));
  }, [actionsConfigOverrides]);
  const contentRowActions = useMemo(() => {
    return Object.keys(allActions).map(actionId => {
      const {
        displayPermissions: contentDisplayPermissions,
        enablePermissions: contentEnablePermissions,
        id,
        name,
        ModalOrPanel,
        tableRowActionGroup,
        orderInTableRowActionGroup,
        TableRowCustomActionButton,
        DisabledTooltipContent,
        onActionButtonClick,
        permissionsExtraData: permissionsExtraDataForAction,
        lockPermissions
      } = allActions[actionId];
      let displayPermissions = undefined;
      const permissionsExtraData = Object.assign({}, permissionsExtraDataForPlatform, permissionsExtraDataForAction);
      if (contentDisplayPermissions) {
        const displayPermissionsWithoutCustomFunction = _objectWithoutPropertiesLoose(contentDisplayPermissions, _excluded);
        displayPermissions = displayPermissionsWithoutCustomFunction;
        if (contentDisplayPermissions.custom) {
          displayPermissions.custom = _ref => {
            let {
                crmObject
              } = _ref,
              rest = _objectWithoutPropertiesLoose(_ref, _excluded2);
            return contentDisplayPermissions.custom(Object.assign({}, rest, {
              crmObjects: [crmObject],
              permissionsExtraData
            }));
          };
        }
      }
      let enablePermissions = undefined;
      if (contentEnablePermissions) {
        const enablePermissionsWithoutCustomFunction = _objectWithoutPropertiesLoose(contentEnablePermissions, _excluded3);
        enablePermissions = enablePermissionsWithoutCustomFunction;
        if (contentEnablePermissions.custom) {
          enablePermissions.custom = _ref2 => {
            let {
                crmObject
              } = _ref2,
              rest = _objectWithoutPropertiesLoose(_ref2, _excluded4);
            return contentEnablePermissions.custom(Object.assign({}, rest, {
              crmObjects: [crmObject],
              permissionsExtraData
            }));
          };
        }
      }
      return {
        id,
        name,
        actionGroup: tableRowActionGroup,
        orderInActionGroup: orderInTableRowActionGroup,
        ModalOrPanel: ModalOrPanel ? withConvertingCrmObjectPropToArray(ModalOrPanel) : undefined,
        displayPermissions,
        enablePermissions,
        ActionButton: TableRowCustomActionButton || (lockPermissions ? buttonProps => /*#__PURE__*/_jsx(LockedActionButton, Object.assign({}, buttonProps, {
          scopes: scopes,
          gates: gates,
          permissionsExtraData: permissionsExtraData,
          lockPermissions: lockPermissions,
          onActionButtonClick: onActionButtonClick
        })) : undefined),
        DisabledTooltipContent: DisabledTooltipContent ? withConvertingCrmObjectPropToArray(DisabledTooltipContent, {
          permissionsExtraData
        }) : undefined,
        onConfirm: onActionButtonClick
      };
    });
  }, [allActions, permissionsExtraDataForPlatform, gates, scopes]);
  const contentBulkActions = useMemo(() => {
    return Object.values(allActions).filter(action => action.isAvailableAsBulkAction).sort((a, b) => (a.orderInBulkActionGroup || 0) - (b.orderInBulkActionGroup || 0));
  }, [allActions]);
  const mergedActionSuccessUpdateHandlers = useActionSuccessUpdateHandlers({
    actionSuccessHandlerOverrides,
    allActions
  });
  const onActionSuccessWithDataReconciliation = useCallback(_ref3 => {
    let {
        actionId,
        data
      } = _ref3,
      rest = _objectWithoutPropertiesLoose(_ref3, _excluded5);
    const actionSuccessUpdateHandler = mergedActionSuccessUpdateHandlers[actionId];
    // Assign `callbackData` only when we know that the action was handled through CMP and its API -
    // otherwise, we should skip the standard action'ssuccess update handler, relying on consumer
    // to handle the success reconciliation.
    const callbackData = data ? {
      completedResults: data.completedResults,
      actionData: data.actionData || {}
    } : undefined;
    if (actionSuccessUpdateHandler && callbackData) {
      actionSuccessUpdateHandler(callbackData);
    }
    onActionSuccess(Object.assign({
      actionId,
      data: callbackData
    }, rest));
  }, [onActionSuccess, mergedActionSuccessUpdateHandlers]);
  return useMemo(() => ({
    allActions,
    contentRowActions,
    contentBulkActions,
    onActionError,
    onActionSuccess: onActionSuccessWithDataReconciliation,
    primaryActionId,
    secondaryActionId,
    isBulkUpdateErrorsModalOpen,
    setIsBulkUpdateErrorsModalOpen,
    bulkUpdateErrorsData,
    setBulkUpdateErrorsData
  }), [allActions, contentRowActions, contentBulkActions, onActionError, primaryActionId, secondaryActionId, isBulkUpdateErrorsModalOpen, bulkUpdateErrorsData, onActionSuccessWithDataReconciliation]);
};
export const ContentActionsContext = /*#__PURE__*/createContext(undefined);
const BaseContentActionsProvider = ({
  onActionSuccess,
  onActionError,
  actionsConfigOverrides,
  primaryActionId,
  secondaryActionId,
  actionSuccessHandlerOverrides,
  children,
  useActionSuccessUpdateHandlers
}) => {
  const value = useContentActionsValue({
    actionsConfigOverrides,
    onActionError,
    onActionSuccess,
    actionSuccessHandlerOverrides,
    primaryActionId,
    secondaryActionId,
    useActionSuccessUpdateHandlers
  });
  return /*#__PURE__*/_jsxs(ContentActionsContext.Provider, {
    value: value,
    children: [children, value.isBulkUpdateErrorsModalOpen ? /*#__PURE__*/_jsx(BulkUpdateErrorsListModal, {}) : null]
  });
};
export const ContentActionsProvider = _ref4 => {
  let {
      children
    } = _ref4,
    rest = _objectWithoutPropertiesLoose(_ref4, _excluded6);
  return /*#__PURE__*/_jsx(PermissionsExtraDataProvider, {
    children: /*#__PURE__*/_jsx(BaseContentActionsProvider, Object.assign({}, rest, {
      children: children
    }))
  });
};

/**
 * This provider is used to set up CMP content actions in the editor.
 * There's no concept of row/bulk actions in the editor, so we can just
 * pass through default values. We can also skip wrapping this in the
 * PermissionsExtraDataProvider since that's only used for the row/bulk actions.
 */
export const EditorContentActionsProvider = _ref5 => {
  let {
      children
    } = _ref5,
    overrides = _objectWithoutPropertiesLoose(_ref5, _excluded7);
  return /*#__PURE__*/_jsx(ContentActionsContext.Provider, {
    value: Object.assign({
      allActions: {},
      contentRowActions: [],
      contentBulkActions: [],
      isBulkUpdateErrorsModalOpen: false,
      setIsBulkUpdateErrorsModalOpen: () => {},
      bulkUpdateErrorsData: {
        results: [],
        actionId: ''
      },
      setBulkUpdateErrorsData: () => {},
      onActionError: () => {},
      onActionSuccess: () => {}
    }, overrides),
    children: children
  });
};
export const useContentActions = () => {
  const context = useContext(ContentActionsContext);
  if (context === undefined) {
    throw new Error('useContentActions must be used within a ContentActionsProvider');
  }
  return context;
};